import { Box, FormControlLabel, Typography } from '@mui/material';
import React, { FunctionComponent, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { ToggleSwitch } from '@Components/index';
import Modal from '@Components/Modal';
import {
  useChangeCallRecordingStateMutation,
  useGetCallRecordingStateQuery,
} from '@Services/settings.service';
import { BaseAlertProps } from '@Typings/global.types';
import useAlert from '@Utils/hooks/useAlert.hook';

import { CallRecordingModalProps } from './CallRecordingModal.types';

const CallRecordingModal: FunctionComponent<CallRecordingModalProps> = (
  props: CallRecordingModalProps
): JSX.Element => {
  const { isOpen, setIsOpen, userId, userName } = props;

  const { t } = useTranslation();
  const { triggerAlert } = useAlert();

  const [isCallRecordingEnabled, setIsCallRecordingEnabled] =
    useState<boolean>(false);
  const [alertState, setAlertState] = useState<BaseAlertProps>({
    openAlert: false,
  });

  // load call recording settings
  const {
    data: dataOfCallRecording = false,
    isLoading: loadingCallRecording,
    isFetching: fetchingCallRecording,
    isError: isErrorForGetCallRecording,
    error: errorForGetCallRecording,
  } = useGetCallRecordingStateQuery(userId);

  // change call recording settings value by user id
  const [changeCallRecordingSetting, changeCallRecordingSettingMethods] =
    useChangeCallRecordingStateMutation();

  useEffect(() => {
    setIsCallRecordingEnabled(dataOfCallRecording);
  }, [dataOfCallRecording]);

  const isLoading = loadingCallRecording || fetchingCallRecording;

  /**
   * Error and Success alerts for changing call recording settings
   */
  useEffect(() => {
    if (changeCallRecordingSettingMethods.error) {
      triggerAlert(changeCallRecordingSettingMethods.error, setAlertState);
    }
  }, [
    changeCallRecordingSettingMethods.error,
    triggerAlert,
    changeCallRecordingSettingMethods,
  ]);

  /**
   * Error Alerts for the Get recording settings
   */
  useEffect(() => {
    if (isErrorForGetCallRecording && errorForGetCallRecording) {
      triggerAlert(errorForGetCallRecording, setAlertState);
    }
  }, [triggerAlert, errorForGetCallRecording, isErrorForGetCallRecording]);

  const handleToggleChange = (
    _event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean
  ) => {
    setIsCallRecordingEnabled(checked);
    return changeCallRecordingSetting(userId);
  };

  return (
    <>
      <Modal
        open={isOpen}
        headerText={t('userCallerIds.Call Recording Settings')}
        onClose={() => setIsOpen(false)}
        alertState={alertState}
      >
        <Box
          display="flex"
          flexDirection="column"
          justifyContent="center"
          alignItems="center"
          sx={{ padding: '3rem 2.4rem 2.4rem' }}
        >
          <Typography variant="main" mb={4} fontSize="16px">
            <Trans
              values={{
                action: dataOfCallRecording ? 'disable' : 'enable',
                userName,
              }}
              i18nKey="userCallerIds.Call Recording Message"
              components={{
                strong: <strong />,
              }}
            />
          </Typography>
          <Typography variant="main">
            {t('userCallerIds.Call Recording Enabled')}
          </Typography>
          <FormControlLabel
            disabled={isLoading}
            control={
              <ToggleSwitch
                checked={isCallRecordingEnabled}
                onChange={handleToggleChange}
              />
            }
            label={
              <Typography variant="main">
                {t(
                  isCallRecordingEnabled
                    ? 'common.messages.Yes'
                    : 'common.messages.No'
                )}
              </Typography>
            }
          />
        </Box>
      </Modal>
    </>
  );
};

export default CallRecordingModal;
